// Where a scope's theme, palette and accent live. A suite keeps them on its
// Workspace record; Everything (scope `*`) has none, so it keeps them on
// Settings (`look`). Pure, so Shell, the previews and Settings agree.
import type { Hue, PaletteRecord } from '@/lib/colour'
import { ALL } from '@/kinds/workspace/schema'
import { entryOfLayer, layerOfEntry, type LayerSetting } from '@/lib/themeLayers'

export interface Look {
  theme?: string
  palette?: PaletteRecord
  colour?: Hue
}

type Carrier = { theme?: string; palette?: Record<string, string>; colour?: string } | undefined

// A palette belongs to the custom theme (the Catppuccin presets and hand-made ones); a built-in
// theme owns its surface and accent, so a palette left behind by an earlier pick is ignored, or its
// accent would beat the theme's and the hue picker.
const pick = (c: Carrier): Look => ({
  theme: c?.theme,
  palette: paletteFor(c?.theme, c?.palette),
  colour: c?.colour as Hue | undefined,
})

/** The look to draw: the current suite's own, else (Everything, or a slug with no suite) Everything's. */
export const lookOf = (suite: { spec: Carrier } | undefined, everything: Carrier): Look =>
  pick(suite ? suite.spec : everything)

/** A look as a settings patch value: only what is set, so an untouched Everything stays unset. */
export const lookToStore = (l: Look): NonNullable<Carrier> => {
  const out: NonNullable<Carrier> = {}
  if (l.theme) out.theme = l.theme
  if (l.palette && Object.keys(l.palette).length) out.palette = l.palette
  if (l.colour) out.colour = l.colour
  return out
}

/**
 * A person's own look (2026-10-17, v2 2026-10-20): one versioned object on her own Settings record
 * (`person_look`), so it can grow without a migration. `all` is her look for every suite and `suites`
 * her look for one; an entry is the fields of a layer (lib/themeLayers.ts): `theme` (a palette's name
 * or `horizon`), `palette`, `colour`, `brightness`, `glass`, and `studio` for a saved theme. The
 * top-level `studio` is the saved theme she wears for every suite (the Themes app writes it). Unknown
 * fields are kept as they are and read as nothing; v1 entries read as they are.
 */
export interface PersonLook {
  v?: number
  all?: Record<string, unknown>
  suites?: Record<string, Record<string, unknown>>
  [more: string]: unknown
}

/** Her layer for a suite: her entry for it over her entry for all, field by field. */
export const personLayerOf = (own: PersonLook | undefined, slug: string): LayerSetting => {
  const all = layerOfEntry({ ...own?.all, studio: own?.all?.studio ?? own?.studio } as Parameters<
    typeof layerOfEntry
  >[0])
  const one = slug === ALL ? {} : layerOfEntry(own?.suites?.[slug] as Parameters<typeof layerOfEntry>[0])
  return {
    ...all,
    ...Object.fromEntries(Object.entries(one).filter(([, v]) => v !== undefined)),
  }
}

/** Her stored layer itself for one scope (`slug` null = all suites): what that tab edits, not the merge. */
export const personEntryLayer = (own: PersonLook | undefined, slug: string | null): LayerSetting =>
  slug === null
    ? layerOfEntry({ ...own?.all, studio: own?.all?.studio ?? own?.studio } as Parameters<
        typeof layerOfEntry
      >[0])
    : layerOfEntry(own?.suites?.[slug] as Parameters<typeof layerOfEntry>[0])

/** The version this code writes. */
export const PERSON_LOOK_V = 2

/**
 * `own` with one layer set for a suite (`slug`) or for all (null slug); an empty layer clears it. Every
 * field it does not know stays. The saved theme for all suites lives at the top level, where the
 * Themes app keeps it.
 */
export const withPersonLayer = (
  own: PersonLook | undefined,
  slug: string | null,
  layer: LayerSetting,
): PersonLook => {
  const next: PersonLook = { ...own, v: PERSON_LOOK_V }
  const entry = entryOfLayer(layer)
  if (slug === null) {
    const { studio, ...rest } = entry
    if (studio) next.studio = studio
    else delete next.studio
    if (Object.keys(rest).length) next.all = rest
    else delete next.all
  } else {
    const suites = { ...own?.suites }
    if (Object.keys(entry).length) suites[slug] = entry
    else delete suites[slug]
    if (Object.keys(suites).length) next.suites = suites
    else delete next.suites
  }
  return next
}

/** The palette that counts for a theme: only `custom` owns one; a built-in theme ignores a leftover. */
export const paletteFor = <P extends Record<string, string>>(
  theme: string | undefined,
  palette: P | undefined,
): P | undefined => (theme === 'custom' ? palette : undefined)

/**
 * The studio's applied theme (themes studio): `person_look.studio`, a snapshot of the
 * ThemeSpec the person wears and, for "currently using X", the profile it came from.
 * It beats the older three fields for surfaces; absent, the look is what it always was.
 * The spec is kept as given (unknown fields, a newer `v`); `canDraw` (lib/themeStudio.ts) decides.
 */
export interface StudioLook {
  profile?: string
  spec: unknown
}

export const studioOf = (own: PersonLook | undefined): StudioLook | undefined => {
  const s = own?.studio as { profile?: unknown; spec?: unknown } | undefined
  if (!s || typeof s !== 'object' || typeof s.spec !== 'object' || s.spec === null) return undefined
  return { profile: typeof s.profile === 'string' ? s.profile : undefined, spec: s.spec }
}

/** `own` with the studio look set (null clears it); every other field stays. */
export const withStudio = (own: PersonLook | undefined, studio: StudioLook | null): PersonLook => {
  const next: PersonLook = { ...own, v: PERSON_LOOK_V }
  if (studio)
    next.studio = studio.profile ? { profile: studio.profile, spec: studio.spec } : { spec: studio.spec }
  else delete next.studio
  return next
}
